import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { type TrialStatusResponse } from '@/api/ai/type';
import {
  TRIAL_STATUS_QUERY_KEY,
  useAiTrialQuotaQuery,
} from '@/shared/features/ai-quiz-generation/hooks';

const DEFAULT_LIFETIME_LIMIT = 3;

/**
 * 쿼터 제한 유저(비로그인 / FREE plan) AI 무료 체험 관리 훅
 *
 * React Query 캐시를 단일 진실의 원천으로 사용:
 * 1. 모달 열 때 서버 데이터 로딩 (useAiTrialQuotaQuery)
 * 2. 생성 성공 시 decrementRemaining()로 캐시 직접 업데이트
 * 3. BuildPageInitializer가 캐시 구독 중 → 모달 닫혀도 값 유지
 *
 * - 쿼터 제한 유저 (비로그인, FREE): 서버 초기화 + 캐시 mutation
 * - 유료 유저 (BASIC, PRO): 무제한 (API 호출 없음)
 */
export const useAiTrialStatus = () => {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, isQuotaLimited, isQuotaExhausted } =
    useAiTrialQuotaQuery();

  // remaining 감소 함수 (생성 성공 시 호출)
  // React Query 캐시를 직접 업데이트하여 모달 생명주기와 무관하게 값 유지
  const decrementRemaining = useCallback(() => {
    if (!isQuotaLimited) return;

    queryClient.setQueryData<TrialStatusResponse>(
      TRIAL_STATUS_QUERY_KEY,
      old => {
        if (!old) return old;
        return { ...old, remaining: Math.max(old.remaining - 1, 0) };
      },
    );
  }, [isQuotaLimited, queryClient]);

  // 유료 유저: 무제한 (API 호출 없음)
  if (!isQuotaLimited) {
    return {
      lifetimeLimit: DEFAULT_LIFETIME_LIMIT,
      used: 0,
      remaining: -1, // Unlimited indicator
      isQuotaExhausted: false,
      isQuotaLimited: false,
      decrementRemaining: () => {},
      isLoading: false,
      isError: false,
    };
  }

  // API 실패 시 strict 정책: 사용 차단
  const fallbackData = {
    lifetimeLimit: DEFAULT_LIFETIME_LIMIT,
    used: 0,
    remaining: 0,
  };
  const quotaData = data ?? fallbackData;

  return {
    lifetimeLimit: quotaData.lifetimeLimit,
    used: quotaData.used,
    remaining: data?.remaining ?? 0, // API 실패 시 strict: 차단
    isQuotaExhausted,
    isQuotaLimited,
    decrementRemaining,
    isLoading,
    isError,
  };
};
